"use client";

import { motion } from "motion/react";
import { useEffect, useRef, useState, useMemo } from "react";
import { cn } from "@/lib/utils";

interface KeyframeStep {
  filter?: string;
  opacity?: number;
  y?: number;
  x?: number;
  [key: string]: any;
}

export interface BlurTextProps {
  text?: string;
  delay?: number;
  className?: string;
  spanClassName?: string;
  animateBy?: "words" | "letters";
  direction?: "top" | "bottom";
  threshold?: number;
  rootMargin?: string;
  animationFrom?: KeyframeStep;
  animationTo?: KeyframeStep[];
  easing?: (t: number) => number | string;
  onAnimationComplete?: () => void;
  stepDuration?: number;
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "div";
  highlightWords?: string[];
  highlightClassName?: string;
  getWordClassName?: (word: string, index: number) => string | undefined;
}

const buildKeyframes = (from: KeyframeStep, steps: KeyframeStep[]) => {
  const keys = new Set([...Object.keys(from), ...steps.flatMap((s) => Object.keys(s))]);

  const keyframes: Record<string, any[]> = {};
  keys.forEach((k) => {
    keyframes[k] = [from[k], ...steps.map((s) => s[k])];
  });
  return keyframes;
};

export default function BlurText({
  text = "",
  delay = 100,
  className = "",
  spanClassName = "",
  animateBy = "words",
  direction = "bottom",
  threshold = 0.1,
  rootMargin = "0px",
  animationFrom,
  animationTo,
  easing = (t) => t,
  onAnimationComplete,
  stepDuration = 0.35,
  as: Component = "p",
  highlightWords = [],
  highlightClassName = "",
  getWordClassName,
}: BlurTextProps) {
  const elements = useMemo(() => {
    return animateBy === "words" ? text.split(" ") : text.split("");
  }, [text, animateBy]);

  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  const defaultFrom = useMemo(
    () =>
      direction === "top"
        ? { filter: "blur(10px)", opacity: 0, y: -35 }
        : { filter: "blur(10px)", opacity: 0, y: 35 },
    [direction]
  );

  const defaultTo = useMemo(
    () => [
      {
        filter: "blur(4px)",
        opacity: 0.7,
        y: direction === "top" ? 3 : -3,
      },
      { filter: "blur(0px)", opacity: 1, y: 0 },
    ],
    [direction]
  );

  const fromSnapshot = animationFrom ?? defaultFrom;
  const toSnapshots = animationTo ?? defaultTo;

  const stepCount = toSnapshots.length + 1;
  const totalDuration = stepDuration * (stepCount - 1);
  const times = Array.from({ length: stepCount }, (_, i) =>
    stepCount === 1 ? 0 : i / (stepCount - 1)
  );

  const MotionTag = (motion as any)[Component] || motion.p;

  return (
    <MotionTag
      ref={ref}
      className={cn("inline-flex flex-wrap justify-center items-center", className)}
    >
      {elements.map((segment, index) => {
        const animateKeyframes = buildKeyframes(fromSnapshot, toSnapshots);

        const spanTransition: any = {
          duration: totalDuration,
          times,
          delay: (index * delay) / 1000,
          ease: easing,
        };

        const cleanWord = segment.replace(/[^a-zA-Z0-9_-]/g, "");
        const isHighlighted =
          highlightWords.length > 0 &&
          (highlightWords.includes(segment) || highlightWords.includes(cleanWord));

        const customWordClass = getWordClassName ? getWordClassName(segment, index) : undefined;

        return (
          <motion.span
            className={cn(
              "inline-block will-change-[transform,filter,opacity]",
              spanClassName,
              isHighlighted && highlightClassName,
              customWordClass
            )}
            key={index}
            initial={fromSnapshot}
            animate={inView ? animateKeyframes : fromSnapshot}
            transition={spanTransition}
            onAnimationComplete={
              index === elements.length - 1 ? onAnimationComplete : undefined
            }
          >
            {segment === " " ? "\u00A0" : segment}
            {animateBy === "words" && index < elements.length - 1 && "\u00A0"}
          </motion.span>
        );
      })}
    </MotionTag>
  );
}

export { BlurText };
